/* eslint-disable @typescript-eslint/no-explicit-any */
import { convertInstanceConfigToOptions } from "./convert-instance-config-to-options";
import { convertValueFormatTypeToOptions } from "./convert-value-format-type-to-options";
import { getChartKeyOptions } from "./get-chartKey-options";
import { colorSet as defaultColorSet } from "@/constants/colors";
import { Lang, getTranslation } from "@/data/translation";
import { ChartComponentParams, ChartKey } from "@/types/chart";
import { deepMerge } from "@/utils/deep-merge";
import { Chart, ChartOptions } from "chart.js";
import { isMobile, isTablet } from "react-device-detect";

export const getChartOptions = ({
  chartKey,
  locale,
  title,
  valueFormatType,
  currency,
  isStacked,
  instanceDataConfig,
  customColorset,
}: {
  chartKey: ChartKey;
  locale: Lang;
  title?: string;
  valueFormatType?: ChartComponentParams["valueFormatType"];
  currency?: string;
  isStacked?: boolean;
  instanceDataConfig?: ChartComponentParams["instanceDataConfig"];
  customColorset?: ChartComponentParams["customColorset"];
}): ChartOptions<"bar" | "line"> => {
  const colorSet = customColorset || defaultColorSet;
  const { t } = getTranslation({ lang: locale });
  const chartKeyOptions = getChartKeyOptions({ chartKey, valueFormatType, t });
  const baseOptions: ChartOptions<"bar" | "line"> & {
    plugins: { crosshair: any };
  } = {
    aspectRatio: isMobile || isTablet ? 1 : 2,
    hover: {
      mode: "index",
      intersect: false,
    },
    parsing: {
      xAxisKey: "x",
      yAxisKey: "y",
    },
    scales: {
      x: {
        stacked: isStacked,
        grid: {
          display: false,
        },
      },
      y: {
        stacked: isStacked,
        grid: {
          display: true,
        },
        // For Area Chart, and compatible with line and bar
        beginAtZero: true,
      },
    },
    layout: {
      padding: 0,
    },
    interaction: {
      intersect: false,
      mode: "index" as const,
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          generateLabels: function (chart) {
            const defaultLabels =
              Chart.defaults.plugins.legend.labels.generateLabels(chart);
            return defaultLabels.map((label) => ({
              ...label,
              pointStyle: "rect",
              strokeStyle: "rgba(0,0,0,0)",
            }));
          },
        },
      },
      tooltip: {
        padding: 10,
        bodySpacing: 5,
        boxPadding: 5,
        callbacks: {
          labelColor(context) {
            return {
              borderColor: "rgba(0,0,0,0)",
              backgroundColor: colorSet[context.datasetIndex],
            };
          },
        },
      },
      title: {
        display: true,
        text: title,
        font: {
          size: 14,
          weight: "bold",
        },
      },
      subtitle: {
        display: !!currency,
        position: "top",
        align: "start",
        text: currency ? t.currency({ currency }) : "",
        font: {
          size: 12,
          weight: "bold",
        },
      },
      crosshair: {
        line: {
          color: "#ccc",
          width: 1,
        },
        sync: {
          enabled: false,
        },
        zoom: {
          enabled: false,
        },
      },
    },
  };

  let valueFormatTypeOptions = {};
  if (valueFormatType && !instanceDataConfig) {
    valueFormatTypeOptions = convertValueFormatTypeToOptions({
      valueFormatType,
    });
  }

  if (instanceDataConfig) {
    const { scales, plugins } = convertInstanceConfigToOptions({
      instanceDataConfig,
    });
    baseOptions.scales = scales;
    baseOptions.plugins = deepMerge(baseOptions.plugins, plugins);
  }

  return deepMerge.apply(null, [
    baseOptions,
    chartKeyOptions,
    ...(Object.keys(valueFormatTypeOptions).length === 0
      ? []
      : [valueFormatTypeOptions]),
  ]);
};
